import { useState } from 'react'
import CustomTable from '../../../shared/CustomTable'
import CustomPagination from '../../../shared/CustomPagination'
import { claimGenerationColumns } from '../../../constants/EmrContants'
import { useTranslation } from 'react-i18next'
import ReusableModal from '../../../shared/ReuseableModal'
import { FaBriefcaseMedical } from 'react-icons/fa6'
import EmrDetails from '../patient-profile/EMRDetails'
import { fetchData } from '../../../helpers/fetchData'
import toast from 'react-hot-toast'
import { MdSync } from 'react-icons/md'
import { EditIcon, EyeIcon } from '../../../utils/TableActionIcons'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setSelectPatientData } from '../../../redux/slices/BeneficiarySlice'
import { setSelectedVisitData } from '../../../redux/slices/VisitsSlice'
import ClaimGenerationFilters from './ClaimGenerationFilters'
import DropdownField from '../../../shared/DropdownField'
import { FiSend } from 'react-icons/fi'
import CustomButton from '../../../shared/CustomButton'
import { getTabState } from '../../../hooks/useTabPersistence'
import CheckPermission from '../../../hooks/CheckPermission'

function ClaimGenerationTable({
  payload,
  setPayload,
  applyFilters,
  resetFilters,
  onPageChange,
  data,
  loading,
  fetchData: initialFetch
}) {
  const { t, i18n } = useTranslation()
  const { hasPermission } = CheckPermission()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedEMRData, setSelectEmrData] = useState(null)
  const [processLoading, setProcessLoading] = useState(false)
  const [viewEmrLoading, setViewEmrLoading] = useState(false)
  const [openViewEmrModal, setOpenViewEmrModal] = useState(false)
  const [bulkLoading, setBulkLoading] = useState(false)
  const onPageChangeHandler = async (newPage, newRowsPerPage) => {
    onPageChange(newPage, newRowsPerPage)
  }

  const handleViewEmrDetails = async (emrNumber, patientId, visitId) => {
    setViewEmrLoading(true)
    let payload = { emr_id: Number(emrNumber), patient_id: patientId, visit_id: visitId }
    let response = await fetchData('POST', '/v3/emr/getById', payload)
    setViewEmrLoading(false)
    if (response?.data) {
      setOpenViewEmrModal(true)
      setSelectEmrData(response?.data)
    } else {
      setSelectEmrData(null)
    }
  }
  const bulkSending = async () => {
    if (!hasPermission('cliamgeneration', 'update')) return
    setBulkLoading(true)
    try {
      const res = await fetchData('POST', 'v3/invoices/processBulkPharmacyClaims', {
        ...payload
      })
      if (res) {
        toast.success(res?.message)
        initialFetch()
      }
    } catch (e) {
      console.log(e)
    }
    setBulkLoading(false)
  }

  const tableActionsTemplate = rowData => (
    <div className={`flex items-center justify-center`}>
      {hasPermission('cliamgeneration', 'update') && (
        <MdSync
          size={22}
          className='text-primary cursor-pointer'
          onClick={async () => {
            if (!hasPermission('cliamgeneration', 'update')) return
            setProcessLoading(true)
            try {
              const res = await fetchData('POST', 'v3/invoices/processClaim', {
                visit_id: rowData?.visit_id
              })
              if (res) {
                toast.success(res?.message)
                initialFetch()
              }
            } catch (e) {
              console.log(e)
            } finally {
              setProcessLoading(false)
            }
          }}
          title={t('ProcessClaim')}
        />
      )}

      {rowData?.emr_numbers?.length > 0 && (
        <div className='flex items-center justify-center'>
          {hasPermission('emr', 'update') && (
            <EditIcon
              title={t('EditEmr')}
              onClick={() => {
                let patientData = {
                  ...rowData?.patient,
                  visit_id: rowData?.visit_id
                }
                dispatch(setSelectPatientData(patientData))
                dispatch(setSelectedVisitData(rowData))

                const tabState = getTabState('claims_batch_flow_tabs')

                navigate('/patient-profile/edit-emr', {
                  state: {
                    emr_id: rowData?.emr_numbers?.[0],
                    visitData: rowData,
                    returnTo: '/claims',
                    previousState: tabState
                  }
                })
              }}
            />
          )}

          {hasPermission('emr', 'get') && (
            <EyeIcon
              onClick={() => handleViewEmrDetails(rowData?.emr_numbers?.[0], rowData?.patient?.patient_id, rowData?.visit_id)}
            />
          )}
                    <DropdownField
            useFormik={false}
            className=' !mb-0 i-card-dropdownfield !w-fit !h-[15px] mx-2'
            options={rowData?.invoice_no?.map((data, index) => {
              return {
                label: data,
                value: data
              }
            })}
            handleChange={event => {
              let value = event?.target?.value
              navigate(`/billing/edit-invoice/${value}`, {
                state: { invoiceData: null, returnTo: '/claim-generation' }
              })
            }}
          />

        </div>
      )}
    </div>
  )

  const actionsColum = {
    header: t('Actions'),
    body: tableActionsTemplate
  }

  const columns = [actionsColum, ...claimGenerationColumns(t, i18n)]
  return (
    <div>
      <div className='my-2 flex items-start justify-between'>
        <ClaimGenerationFilters
          payloadData={payload}
          setPayloadData={setPayload}
          applyFilters={applyFilters}
          resetFilters={resetFilters}
          loading={loading}
          fetchNewData={initialFetch}
        />
        {hasPermission('cliamgeneration', 'update') && (
          <CustomButton
            className='min-w-[200px] max-w-full'
            label={t('Bulk Pharmacy Submission')}
            onClick={bulkSending}
            type='success'
            icon={FiSend}
            isLoading={bulkLoading}
          />
        )}
      </div>
      <CustomTable
        isLoading={loading || processLoading || viewEmrLoading}
        columns={columns}
        data={data?.data || []}
      />
      {data?.data?.length > 0 && (
        <CustomPagination
          currentPage={data?.page}
          rowsPerPage={data?.perPage}
          totalRecords={data?.totalCount}
          totalPages={Math.ceil((data?.totalCount || 0) / (data?.perPage || 1))}
          onPageChange={onPageChangeHandler}
        />
      )}
      <ReusableModal
        position='top'
        isVisible={openViewEmrModal}
        title={t('Medical Summary')}
        HeaderIcon={FaBriefcaseMedical}
        onClose={() => {
          setOpenViewEmrModal(false)
          setSelectEmrData(null)
        }}
        className='w-full lg:w-[850px] relative'
      >
        <EmrDetails emrData={selectedEMRData} />
      </ReusableModal>
    </div>
  )
}

export default ClaimGenerationTable
